/**
 * Güvenlik — Profil > Güvenlik: kullanıcının KENDİ iki adımlı doğrulaması (backlog #640, backend #639). Rota: /profile/guvenlik (her giriş: personel ve firma).
 *
 * Durum `GET /mfa/status`'tan gelir: açık mı, hangi yöntem (SMS | TOTP), maskeli numara, SMS seçeneği sunulabilir mi (`smsAvailable`: System Settings anahtarı ya da
 * sunucuda OTP anahtarı yoksa false → SMS seçeneği HİÇ gösterilmez, yalnız kimlik doğrulayıcı), profil numarası kullanılabilir mi, giriş kilidi.
 *   · kapalıyken: SMS kur (varsa) / kimlik doğrulayıcı kur
 *   · açıkken: numarayı değiştir / başka yönteme geç / kimlik doğrulayıcıyı yeniden kur / kapat — hepsi MEVCUT ŞİFRE ister (çalınmış bir oturum ikinci faktörü
 *     değiştiremesin); yanlış şifre sayılır, 5 yanlış kilitler (429)
 * Gizli anahtar ve şifre yalnız bileşen belleğinde durur ve kurulum bitince/iptalde atılır; hiçbiri günlüğe, adrese ya da depolamaya yazılmaz.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { mfaDurumu, mfaKapat } from '../../../services/mfaService';
import SmsKurulumu from './SmsKurulumu';
import TotpKurulumu from './TotpKurulumu';
import { kalanDakika, mfaMesaji, saatMetni } from './mfaMesajlari';

const GuvenlikPage = () => {
  const { t } = useTranslation('giris');
  const [durum, setDurum] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  // 'ozet' | 'sms' | 'totp' | 'kapat'
  const [adim, setAdim] = useState('ozet');
  const [mesaj, setMesaj] = useState(null);

  useEffect(() => { document.title = `${t('mfa.guvenlik.baslik')} | FDS`; }, [t]);

  const yenile = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      const sonuc = await mfaDurumu();
      if (sonuc.ok) setDurum(sonuc.data);
      else setHata(mfaMesaji(t, sonuc));
    } catch {
      setHata(t('mfa.hata.baglanti'));
    } finally {
      setYukleniyor(false);
    }
  }, [t]);

  // KURAL (react-hooks/set-state-in-effect): state, effect gövdesinde değil async fonksiyonun içinde değişir.
  useEffect(() => {
    let iptal = false;
    async function calistir() {
      try {
        const sonuc = await mfaDurumu();
        if (iptal) return;
        if (sonuc.ok) setDurum(sonuc.data);
        else setHata(mfaMesaji(t, sonuc));
      } catch {
        if (!iptal) setHata(t('mfa.hata.baglanti'));
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }
    calistir();
    return () => { iptal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bitti = (anahtar) => {
    setAdim('ozet');
    setMesaj(t(anahtar));
    yenile();
  };

  const acik = Boolean(durum && durum.mfaEnabled);
  const sms = acik && durum.mfaMethod === 'SMS';
  const kilitSaati = saatMetni(durum && durum.loginLockedUntil);
  const kilitli = kalanDakika(durum && durum.loginLockedUntil) !== null;

  let icerik;
  if (yukleniyor && !durum) {
    icerik = <div className="text-center py-4"><Spinner /></div>;
  } else if (hata && !durum) {
    icerik = <Alert color="danger" fade={false}>{hata}</Alert>;
  } else if (adim === 'sms') {
    icerik = <SmsKurulumu durum={durum} onBitti={() => bitti('mfa.guvenlik.smsAcildi')} onIptal={() => setAdim('ozet')} />;
  } else if (adim === 'totp') {
    icerik = <TotpKurulumu durum={durum} onBitti={() => bitti('mfa.guvenlik.uygulamaAcildi')} onIptal={() => { setAdim('ozet'); yenile(); }} />;
  } else if (adim === 'kapat') {
    icerik = <KapatFormu onBitti={() => bitti('mfa.guvenlik.kapatildi')} onIptal={() => setAdim('ozet')} />;
  } else {
    icerik = (
      <>
        {mesaj && <Alert color="success" fade={false} data-testid="guvenlik-mesaj" toggle={() => setMesaj(null)}>{mesaj}</Alert>}
        {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
        {kilitli && <Alert color="warning" fade={false} data-testid="guvenlik-kilit">{t('mfa.guvenlik.girisKilitli', { saat: kilitSaati || '' })}</Alert>}
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="fw-semibold">{t('mfa.guvenlik.durum')}</span>
          {acik
            ? <Badge color="success" data-testid="guvenlik-acik">{sms ? t('mfa.guvenlik.acikSms') : t('mfa.guvenlik.acikUygulama')}</Badge>
            : <Badge color="secondary" data-testid="guvenlik-kapali">{t('mfa.guvenlik.kapali')}</Badge>}
        </div>
        <p className="text-muted">{acik ? t('mfa.guvenlik.acikAciklama') : t('mfa.guvenlik.kapaliAciklama')}</p>
        {sms && durum.mfaPhoneMasked && (
          <p className="mb-2">{t('mfa.guvenlik.numara')}: <strong data-testid="guvenlik-numara">{durum.mfaPhoneMasked}</strong></p>
        )}
        {sms && durum.totpFallback && <p className="text-muted small">{t('mfa.guvenlik.uygulamaDaVar')}</p>}
        <div className="d-flex flex-wrap gap-2 mt-3">
          {durum.smsAvailable && (
            <Button color={acik ? 'light' : 'primary'} id="guvenlik-sms" onClick={() => { setMesaj(null); setAdim('sms'); }}>
              {sms ? t('mfa.guvenlik.numaraDegistir') : acik ? t('mfa.guvenlik.smseGec') : t('mfa.guvenlik.smsKur')}
            </Button>
          )}
          <Button color={acik ? 'light' : 'primary'} id="guvenlik-totp" onClick={() => { setMesaj(null); setAdim('totp'); }}>
            {acik && !sms ? t('mfa.guvenlik.uygulamayiYenidenKur') : acik ? t('mfa.guvenlik.uygulamayaGec') : t('mfa.guvenlik.uygulamaKur')}
          </Button>
          {acik && (
            <Button color="danger" outline id="guvenlik-kapat" onClick={() => { setMesaj(null); setAdim('kapat'); }}>{t('mfa.guvenlik.kapat')}</Button>
          )}
        </div>
        {!durum.smsAvailable && !acik && <p className="text-muted small mt-3 mb-0">{t('mfa.guvenlik.smsYok')}</p>}
      </>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t('mfa.guvenlik.baslik')} pageTitle={t('mfa.guvenlik.profil')} />
        <Row>
          <Col lg={8} xl={6}>
            <Card>
              <CardHeader><h5 className="card-title mb-0">{t('mfa.guvenlik.kartBasligi')}</h5></CardHeader>
              <CardBody>{icerik}</CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

/** İkinci faktörü kapat: mevcut şifre ister (yanlış şifre 403 ve sayılır, 5 yanlış 15 dakika kilitler). */
const KapatFormu = ({ onBitti, onIptal }) => {
  const { t } = useTranslation('giris');
  const [sifre, setSifre] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  async function kapat(e) {
    e.preventDefault();
    if (!sifre) return;
    setYukleniyor(true);
    setHata(null);
    try {
      const sonuc = await mfaKapat(sifre);
      if (sonuc.ok) { onBitti(); return; }
      setSifre('');
      setHata(mfaMesaji(t, sonuc));
    } catch {
      setHata(t('mfa.hata.baglanti'));
    } finally {
      setYukleniyor(false);
    }
  }

  return (
    <Form onSubmit={kapat} data-testid="guvenlik-kapat-formu">
      <p className="text-muted">{t('mfa.guvenlik.kapatAciklama')}</p>
      {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
      <div className="mb-3">
        <Label htmlFor="guvenlik-kapat-sifre" className="form-label">{t('mfa.guvenlik.mevcutSifre')}</Label>
        <Input id="guvenlik-kapat-sifre" type="password" autoComplete="current-password" value={sifre} onChange={(e) => setSifre(e.target.value)} />
      </div>
      <div className="d-flex gap-2">
        <Button color="danger" type="submit" disabled={yukleniyor || !sifre}>{yukleniyor ? <Spinner size="sm" /> : t('mfa.guvenlik.kapat')}</Button>
        <Button color="light" type="button" onClick={onIptal}>{t('mfa.guvenlik.vazgec')}</Button>
      </div>
    </Form>
  );
};

export default GuvenlikPage;
